import { makeStyles } from '@material-ui/styles'
import { checkImageLibraryRequiredActions } from 'app/plugins/openstack/components/images/checkImageLibraryRequiredActions'
import {
  listHypervisors,
  listResmgrHosts,
} from 'app/plugins/openstack/components/infrastructure/actions'
import { listAvailabilityZones } from 'app/plugins/openstack/components/infrastructure/availability-zones/actions'
import { resmgrHostsSelector } from 'app/plugins/openstack/components/infrastructure/selectors'
import { IHealthServiceDetailsPageTabs } from 'app/plugins/openstack/components/networks/model'
import { useAppSelector } from 'app/store'
import ActionRequiredModalTrigger, {
  ActionRequiredIssue,
} from 'core/components/ActionRequiredModalTrigger'
import DocumentMeta from 'core/components/DocumentMeta'
import PollingData from 'core/components/PollingData'
import Badge from 'core/elements/badge'
import { createGridStatusCell } from 'core/elements/grid/cells/GridStatusCell'
import Tabs from 'core/elements/tabs'
import Tab from 'core/elements/tabs/Tab'
import Text from 'core/elements/Text'
import Tooltip from 'core/elements/tooltip'
import useListAction from 'core/hooks/useListAction'
import { useLocalNetworkAccessAllowed } from 'core/hooks/useLocalNetworkAccessAllowed'
import Theme from 'core/themes/model'
import { getProductName } from 'core/utils/helpers'
import { routeManager } from 'core/utils/routes'
import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  listComputeServiceDetails,
  listImageServiceDetails,
  listNetworkServiceDetails,
  listStorageServiceDetails,
} from './action'
import { ServiceType } from './constant'
import { useControlPlaneServicesCheck } from './ControlPlaneServicesContext'
import ControlPlaneServicesHealth from './ControlPlaneServicesHealth'
import { HealthStatusBadge } from './HealthStatusBadge'
import {
  aggregateServiceStateOnServiceType,
  aggregateServiceStates,
  buildCompleteCombinedServices,
  getConnectivityStatus,
  getHealthStatus,
  getMergedStorageServiceDetails,
  getOverallServiceStatus,
  hostStateCountHelper,
  IPAddressCellComponent,
  serviceStateCountHelper,
  validServiceDetails,
} from './helper'
import {
  combinedServicesSelector,
  computeServicesSelector,
  imageServicesSelector,
  networkServicesSelector,
  ServiceStatus,
  storageServicesSelector,
} from './selector'
import ServiceHealthHeaderCard, { ServiceHealthHeaderType } from './ServiceHealthHeaderCard'
import ServiceHealthHostsListPage from './ServiceHealthHostsListPage'
import ServiceStatusCard from './ServiceStatusCard'

const useStyles = makeStyles((theme: Theme) => ({
  searchBarContainer: {
    marginBottom: theme.spacing(2),
    paddingTop: theme.spacing(2),
  },
  content: {
    display: 'grid',
    gap: theme.spacing(1),
  },
  filterComponent: {
    background: theme.palette.common.white,
    padding: '2px 120px 2px 8px',
    border: `1px solid ${theme.palette.grey[200]}`,
    width: '320px',
    borderRadius: '4px',
    '& input': {
      width: '210px',
    },
  },
  serviceDetails: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr 1fr',
    alignItems: 'center',
    gap: 24,
    paddingBottom: theme.spacing(3),
  },
  serviceHealthList: {
    display: 'grid',
    gap: 8,
  },
  serviceHealth: {
    display: 'grid',
    gap: 4,
  },
}))

const columns = (serviceType: ServiceType, classes, isBrowserLocalNetworkAccessAllowed = true) => [
  {
    key: 'name',
    label: 'Host Name',
  },
  // Cluster Name column is only visible for compute services
  ...(serviceType === ServiceType.Compute
    ? [
        {
          key: 'clusterName',
          label: 'Cluster Name',
        },
      ]
    : []),
  ...(serviceType === ServiceType.Storage
    ? [
        {
          key: 'status',
          label: 'Service Health',
          CellComponent: ({ value, item }) => (
            <div className={classes.serviceHealthList}>
              {item.volumeBackendStatuses?.map((status, idx) => (
                <div key={idx} className={classes.serviceHealth}>
                  <Text variant="caption1">{status.volumeBackend}</Text>
                  <HealthStatusBadge value={status.status} serviceType={serviceType} />
                </div>
              ))}
            </div>
          ),
        },
      ]
    : [
        {
          key: 'status',
          label: 'Service Health',
          CellComponent: ({ value, item }) => {
            const issues: ActionRequiredIssue[] = useMemo(() => {
              if (serviceType !== ServiceType.Image) {
                return []
              }
              const needToValidateCert = value === ServiceStatus.CertificateError
              const glanceHostNotReachable = value === ServiceStatus.NetworkError

              return checkImageLibraryRequiredActions({
                needToValidateCert,
                glanceHostNotReachable,
                isBrowserLocalNetworkAccessAllowed,
                hostEndpoint: item?.endpoint || item?.host,
              })
            }, [isBrowserLocalNetworkAccessAllowed, item?.endpoint, item?.host, value])

            return issues.length > 0 ? (
              <ActionRequiredModalTrigger
                issues={issues}
                modalTitle="Needs Action"
                modalIntroText="The following actions are required to access the Image Library Endpoint:"
                trigger={({ openWithEvent }) => (
                  <Badge
                    variant="warning"
                    text={`Needs Action (${issues.length})`}
                    ellipsisAt={50}
                    onClick={openWithEvent}
                  />
                )}
              />
            ) : (
              <HealthStatusBadge
                value={value}
                serviceType={serviceType}
                endpoint={serviceType === ServiceType.Image ? item?.endpoint : undefined}
              />
            )
          },
        },
      ]),
  {
    key: 'connectivityStatus',
    label: (
      <div title="">
        <Tooltip
          message={`Reports the status of the connection between ${getProductName()} management plane and the host`}
        >
          <span>Connectivity Status</span>
        </Tooltip>
      </div>
    ),
    CellComponent: createGridStatusCell({
      dataFn: (phase: string) => getConnectivityStatus(phase),
    }),
  },
  {
    key: 'roleStatus',
    label: 'Role Status',
    CellComponent: createGridStatusCell({
      dataFn: getHealthStatus,
    }),
  },
  {
    key: 'ipAddresses',
    label: 'IP Addresses',
    CellComponent: IPAddressCellComponent,
  },
]

const RELOAD_INTERVAL = 5 * 60 * 1000 // 5 minutes

const ServiceHealthDetailsPage = () => {
  const classes = useStyles()
  const [lastChecked, setLastChecked] = useState<Date | null>(null)
  const [nextCheck, setNextCheck] = useState<Date | null>(null)
  const [showComputeServices, setShowComputeServices] = useState(false)
  const [showStorageServices, setShowStorageServices] = useState(false)
  const [showNetworkServices, setShowNetworkServices] = useState(false)
  const [showImageServices, setShowImageServices] = useState(false)

  const { reload: reloadHypervisors } = useListAction(listHypervisors)
  const { reload: reloadHosts } = useListAction(listResmgrHosts)
  const { reload: reloadComputeService, loading: computeLoading } = useListAction(
    listComputeServiceDetails,
  )
  const { reload: reloadStorageService, loading: storageLoading } = useListAction(
    listStorageServiceDetails,
  )
  const { reload: reloadNetworkService, loading: networkLoading } = useListAction(
    listNetworkServiceDetails,
  )
  const { reload: reloadImageService, loading: imageLoading } = useListAction(
    listImageServiceDetails,
  )
  const { loading } = useListAction(listAvailabilityZones)

  const computeServiceDetails = useAppSelector(computeServicesSelector)
  const storageServiceDetails = useAppSelector(storageServicesSelector)
  const networkServiceDetails = useAppSelector(networkServicesSelector)
  const imageServiceDetails = useAppSelector(imageServicesSelector)
  const combinedServicesDetails = useAppSelector(combinedServicesSelector)
  const hosts = useAppSelector(resmgrHostsSelector)

  // Filter out services that do not have host details
  const validComputeServiceDetails = useMemo(() => {
    return validServiceDetails(computeServiceDetails)
  }, [computeServiceDetails])
  const validStorageServiceDetails = useMemo(() => {
    return validServiceDetails(storageServiceDetails)
  }, [storageServiceDetails])
  const validNetworkServiceDetails = useMemo(() => {
    return validServiceDetails(networkServiceDetails)
  }, [networkServiceDetails])
  const validImageServiceDetails = useMemo(() => {
    return validServiceDetails(imageServiceDetails)
  }, [imageServiceDetails])
  const validCombinedServicesDetails = useMemo(() => {
    return validServiceDetails(combinedServicesDetails)
  }, [combinedServicesDetails])

  const completeCombinedServices = useMemo(
    () => buildCompleteCombinedServices(validCombinedServicesDetails, hosts),
    [validCombinedServicesDetails, hosts],
  )

  const mergedStorageServiceDetails = useMemo(() => {
    return getMergedStorageServiceDetails(validStorageServiceDetails)
  }, [validStorageServiceDetails])

  // Get the status of each service type
  const computeServiceStates = useMemo(() => serviceStateCountHelper(validComputeServiceDetails), [
    validComputeServiceDetails,
  ])
  const storageServiceStates = useMemo(() => serviceStateCountHelper(validStorageServiceDetails), [
    validStorageServiceDetails,
  ])
  const networkServiceStates = useMemo(() => serviceStateCountHelper(validNetworkServiceDetails), [
    validNetworkServiceDetails,
  ])
  const imageServiceStates = useMemo(() => serviceStateCountHelper(validImageServiceDetails), [
    validImageServiceDetails,
  ])
  const hostStates = useMemo(() => hostStateCountHelper(completeCombinedServices), [
    completeCombinedServices,
  ])

  const computeServiceState = getOverallServiceStatus(
    computeServiceStates,
    true,
    validComputeServiceDetails,
  )
  const storageServiceState = getOverallServiceStatus(storageServiceStates)
  const networkServiceState = getOverallServiceStatus(networkServiceStates)
  const imageServiceState = getOverallServiceStatus(imageServiceStates)

  const isBrowserLocalNetworkAccessAllowed = useLocalNetworkAccessAllowed()

  // Get the first image service endpoint for certificate validation
  const imageHostEndpoint = useMemo(() => {
    if (validImageServiceDetails?.length > 0) {
      return validImageServiceDetails[0]?.endpoint || validImageServiceDetails[0]?.host
    }
    return undefined
  }, [validImageServiceDetails])

  // Aggregate the states of all services based on service type
  const aggregatedStates = aggregateServiceStateOnServiceType([
    computeServiceState,
    storageServiceState,
    networkServiceState,
    imageServiceState,
  ])

  // Aggregate the states based on individual services
  const aggregatedHostStates = aggregateServiceStates([hostStates])

  // Set the last checked time and next check time
  const setRefreshTimings = useCallback(() => {
    const now = new Date()
    setLastChecked(now)
    const next = new Date(now.getTime() + RELOAD_INTERVAL)
    setNextCheck(next)
  }, [])

  const handleServiceReloads = useCallback(async (refetch = false, updateLoadingState = true) => {
    setRefreshTimings()
    reloadComputeService(refetch, updateLoadingState)
    reloadStorageService(refetch, updateLoadingState)
    reloadNetworkService(refetch, updateLoadingState)
    reloadImageService(refetch, updateLoadingState)
    reloadControlPlaneServices(refetch, true)
  }, [])

  // Load control plane service health
  const {
    serviceHealthStates: controlPlaneServiceStates,
    isLoading,
    reload: reloadControlPlaneServices,
    servicesList,
  } = useControlPlaneServicesCheck()

  // Load initial data
  useEffect(() => {
    handleServiceReloads(true, true)
    reloadHosts()
    reloadHypervisors()
    reloadControlPlaneServices(true, true)
  }, [])

  return (
    <>
      <DocumentMeta title="Service Health" breadcrumbs />
      <PollingData
        loading={loading}
        onReload={handleServiceReloads}
        refreshDuration={RELOAD_INTERVAL}
        hidden
      />
      <div className={classes.serviceDetails}>
        <ServiceHealthHeaderCard
          type={ServiceHealthHeaderType.HypervisorServices}
          title="Service Health"
          statusDetails={aggregatedStates}
        />
        <ServiceHealthHeaderCard
          type={ServiceHealthHeaderType.HostServices}
          title="Host Health"
          statusDetails={aggregatedHostStates}
        />
        <ServiceHealthHeaderCard
          type={ServiceHealthHeaderType.ControlPlaneServices}
          title="API Endpoints Health"
          statusDetails={controlPlaneServiceStates}
        />
      </div>
      <Tabs route={routeManager.routes.openstack.health.details}>
        <Tab
          label={`Service Health (${aggregatedStates?.total})`}
          value={IHealthServiceDetailsPageTabs.ServiceHealth}
        >
          <div className={classes.content}>
            <ServiceStatusCard
              iconName={'hard-drive'}
              headerName={'Compute Service'}
              headerSubtext={'Handles virtual machine provisioning and management'}
              successCount={computeServiceStates?.healthy}
              errorCount={computeServiceStates?.unhealthy}
              warningCount={
                computeServiceStates?.maintenance + computeServiceStates?.schedulingDisabled
              }
              lastChecked={lastChecked}
              nextCheck={nextCheck}
              showAdvancedOptions={showComputeServices}
              setShowAdvancedOptions={setShowComputeServices}
              data={validComputeServiceDetails}
              columns={columns(ServiceType.Compute, classes)}
              loading={computeLoading}
              emptyContent="No compute services available"
              statusHealth={computeServiceState?.statusHealth}
              statusName={computeServiceState?.statusName}
              serviceType={ServiceType.Compute}
            />
            <ServiceStatusCard
              iconName={'images'}
              headerName={'Image Service'}
              headerSubtext={'Manages VM images and snapshots'}
              successCount={imageServiceStates?.healthy}
              errorCount={imageServiceStates?.unhealthy}
              warningCount={imageServiceStates?.networkError}
              lastChecked={lastChecked}
              nextCheck={nextCheck}
              showAdvancedOptions={showImageServices}
              setShowAdvancedOptions={setShowImageServices}
              data={validImageServiceDetails}
              columns={columns(ServiceType.Image, classes, isBrowserLocalNetworkAccessAllowed)}
              loading={imageLoading}
              emptyContent="No image services available"
              statusHealth={imageServiceState?.statusHealth}
              statusName={imageServiceState?.statusName}
              serviceType={ServiceType.Image}
              hostEndpoint={imageHostEndpoint}
            />
            <ServiceStatusCard
              iconName={'coins'}
              headerName={'Persistent Storage Service'}
              headerSubtext={'Provides persistent block storage for virtual machines'}
              successCount={storageServiceStates?.healthy}
              errorCount={storageServiceStates?.unhealthy}
              lastChecked={lastChecked}
              nextCheck={nextCheck}
              showAdvancedOptions={showStorageServices}
              setShowAdvancedOptions={setShowStorageServices}
              data={mergedStorageServiceDetails}
              columns={columns(ServiceType.Storage, classes)}
              loading={storageLoading}
              emptyContent="No storage services available"
              statusHealth={storageServiceState?.statusHealth}
              statusName={storageServiceState?.statusName}
              serviceType={ServiceType.Storage}
            />
            <ServiceStatusCard
              iconName={'network-wired'}
              headerName={'Networking Service'}
              headerSubtext={'Manages virtual networks, subnets, and routing'}
              successCount={networkServiceStates?.healthy}
              errorCount={networkServiceStates?.unhealthy}
              lastChecked={lastChecked}
              nextCheck={nextCheck}
              showAdvancedOptions={showNetworkServices}
              setShowAdvancedOptions={setShowNetworkServices}
              data={validNetworkServiceDetails}
              columns={columns(ServiceType.Network, classes)}
              loading={networkLoading}
              emptyContent="No network services available"
              statusHealth={networkServiceState?.statusHealth}
              statusName={networkServiceState?.statusName}
              serviceType={ServiceType.Network}
            />
          </div>
        </Tab>
        <Tab
          label={`Host Health (${aggregatedHostStates?.total})`}
          value={IHealthServiceDetailsPageTabs.HostHealth}
        >
          <ServiceHealthHostsListPage />
        </Tab>
        <Tab
          label={`API Endpoints Health (${controlPlaneServiceStates?.total})`}
          value={IHealthServiceDetailsPageTabs.ControlPlaneServicesHealth}
        >
          <ControlPlaneServicesHealth />
        </Tab>
      </Tabs>
    </>
  )
}

export default ServiceHealthDetailsPage
